import { Data, DateUtils, FileUtils, List } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { AppService, DomainTypes, Types } from '../../DomainTypes';
import NewPaymentExportCommand from '../../Models/PaymentExport/Lookups/NewPaymentExportCommand';
import PaymentExportDetailsPagedCriteria from '../../Models/PaymentExport/Lookups/PaymentExportDetailsPagedCriteria';
import PaymentExportPagedCriteria from '../../Models/PaymentExport/Lookups/PaymentExportPagedCriteria';
import PaymentExportLookup from '../../Models/PaymentExport/Lookups/PaymentExportLookup';
import PaymentExportDetailLookup from '../../Models/PaymentExport/Lookups/PaymentExportDetailLookup';
import { NotificationDuration } from '../../../App/Models/NotificationDuration';
import { PaymentType } from '../../Models/PaymentExport/PaymentType';
import TextValues from '../../Services/TextValues';

export default class PaymentExportVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public dataCache = AppService.get(DomainTypes.Services.DataCache),
        public authorisation = AppService.get(Types.Neo.Security.AuthorisationService),
        protected paymentExportApiClient = AppService.get(Types.Domain.ApiClients.PaymentExport)) {

        super(taskRunner);
        this.makeObservable();
        this.autoDispose(this.exportCriteria.onAnyPropertyChanged(() => this.pageManager.refreshData()));
    }

    public exportCriteria = new PaymentExportPagedCriteria();
    public exportDetailCriteria = new PaymentExportDetailsPagedCriteria();
    public currentExport: PaymentExportLookup | null = null;
    public newPaymentExport: NewPaymentExportCommand | null = null;
    public paymentTypes: { id: number; name: string }[] = [];

    public pageManager = new Data.PageManager(this.exportCriteria, PaymentExportLookup, this.paymentExportApiClient.getPaymentExportsPaged, {
        pageSize: 20,
        taskRunner: this.taskRunner,
        fetchInitial: true,
        allowSort: true,
        sortAscending: false,
        sortBy: "paymentDate",
    });

    public detailsPageManager = new Data.PageManager(this.exportDetailCriteria, PaymentExportDetailLookup, this.paymentExportApiClient.getPaymentExportDetailsPaged, {
        pageSize: 20,
        taskRunner: this.taskRunner,
        fetchInitial: false,
        allowSort: true,
    });

    public async initialise() {
        this.paymentTypes = [
            { id: PaymentType.Dividend, name: TextValues.distribution },
            { id: PaymentType.Capital, name: "Capital" },
        ];
    }

    public async addNewPaymentExport(paymentType: PaymentType) {
        const response = await this.taskRunner.waitFor(this.paymentExportApiClient.getNextSuggestedPaymentExportStartDate(paymentType));
        const newExport = new NewPaymentExportCommand();
        newExport.startDate = new Date(response.data);
        newExport.paymentType = paymentType;
        if (newExport.endDate <= newExport.startDate) {
            newExport.endDate = DateUtils.add(newExport.startDate, { months: 1 });
        }
        this.newPaymentExport = newExport;
    }

    public async addMissingRecords(paymentExport: PaymentExportLookup) {
        const command = new NewPaymentExportCommand();
        command.paymentType = paymentExport.paymentType;
        command.startDate = paymentExport.paymentDate;
        command.endDate = paymentExport.paymentDate;
        command.entityId = paymentExport.entityId;
        
        await this.saveNewPaymentExport(command);
    }

    public async createNewPaymentExport() {
        if (this.newPaymentExport) {
            await this.saveNewPaymentExport(this.newPaymentExport);
            this.resetNewPayment();
        }
    }

    public async saveNewPaymentExport(command: NewPaymentExportCommand) {
        
        await this.taskRunner.run(async () => {
            const response = await this.paymentExportApiClient.saveNewPaymentExport(command.toQueryObject());

            const newBatches = List.fromJSArray(PaymentExportLookup, response.data);
            this.pageManager.data.push(...newBatches.filter(c => !c.previouslyCreated));

            if (newBatches.length === 1) {
                this.setPaymentExport(newBatches[0]);
            } else if (newBatches.some(c => c.previouslyCreated)) {
                this.notifications.addInfo("Batches Created", "Some records were added to existing batches.", NotificationDuration.Standard);
            }  
        });
    }

    public resetNewPayment() {
        this.newPaymentExport = null;
    }

    public async loadPaymentExport(paymentExportId: number) {
        
        const data = await this.taskRunner.waitForData(this.paymentExportApiClient.getPaymentExportLookup(paymentExportId));
        this.setPaymentExport(PaymentExportLookup.fromJSObject<PaymentExportLookup>(data));
    }

    private setPaymentExport(paymentExport: PaymentExportLookup) {
        this.currentExport = paymentExport;
        this.exportDetailCriteria.paymentExportId = paymentExport.paymentExportId;
        this.detailsPageManager.refreshData();
    }

    public back() {
        this.currentExport = null;
        this.exportDetailCriteria.paymentExportId = null;
    }

    public async deletePaymentExport(lookup: PaymentExportLookup) {
        await this.taskRunner.run(async () => {
            await this.paymentExportApiClient.deletePaymentExport(lookup.paymentExportId);
            await this.pageManager.refreshData();
            this.notifications.addSuccess("Payment Export Deleted", "Payment export batch was deleted successfully", NotificationDuration.Standard);
        });
    }

    public async deletePaymentExportDetail(detail: PaymentExportDetailLookup) {
        await this.taskRunner.run(async () => {
            if (this.currentExport) {
                await this.paymentExportApiClient.deletePaymentExportDetail(detail.paymentExportDetailId);
                this.detailsPageManager.data.removeWithoutTracking(detail);
                this.notifications.addSuccess("Payment Export Detail Deleted", "Payment export detail was deleted successfully", NotificationDuration.Standard);
            }
        });
    }

    public async export() {
        const blobData = await this.getExportFile();
        if (blobData) {
            FileUtils.showSaveFile(blobData);
            this.back();
            this.pageManager.refreshData();
        }
    }

    private async getExportFile() {
        if (this.currentExport) {
            const response = await this.taskRunner.waitFor(
                this.paymentExportApiClient.export(this.currentExport.paymentExportId),
                { allowPost: true });

            this.currentExport.isExported = true;
            return response;
        }
        return null;
    }
}